import { type RefObject, useEffect } from 'react';

/** When the composer's field takes the caret without anyone clicking it. */
export function useComposerFocus(
  field: RefObject<HTMLTextAreaElement | null>,
  focusOn: string | undefined,
  disabled: boolean,
) {
  /*
   * A hidden field cannot take the caret, and asking it to is not an error —
   * the browser ignores it — so the closed drawer needs no special case.
   */
  useEffect(() => {
    field.current?.focus();
  }, [field, focusOn]);

  /*
   * The caret goes back to the field the moment a reply has landed, which is
   * the moment the next thing is written.
   */
  useEffect(() => {
    if (!disabled) {
      field.current?.focus();
    }
  }, [field, disabled]);

  /*
   * Coming back to a window with nothing in particular focused lands in the
   * conversation, as in every messenger. Somebody who left mid-sentence in a
   * form elsewhere on the page is returned there by the browser instead.
   */
  useEffect(() => {
    function onWindowFocus() {
      if (!disabled && document.activeElement === document.body) {
        field.current?.focus();
      }
    }

    window.addEventListener('focus', onWindowFocus);

    return () => window.removeEventListener('focus', onWindowFocus);
  }, [field, disabled]);
}
